import { useCallback, useEffect } from 'react';
import { Path, useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { DispositionsDiagram_DispositionProvisionTerminalFragment } from '@/modules/entityDiagram/graphql/DispositionsDiagram.generated';
import {
  ClientOrganizationKind,
  DispositiveProvisionRecipientKind,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import { useDispositiveDiagramSyncContext } from '../contexts/DispositiveDiagramSync.context';
import { useDispositiveProvisionsContext } from '../contexts/dispositiveProvisions.context';
import { DispositiveProvisionsFormRecipient } from '../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { useRecipientIdMetadataMap } from '../DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DraggableListItemRecipient.utils';
import { SURVIVING_SPOUSE_SENTINEL } from '../DispositiveProvisionsForm/hooks/useRecipientOptions';
import { DispositiveProvisionsFormV2Shape } from '../DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';

function getDispositionKindForFormRecipient(
  r: DispositiveProvisionsFormRecipient
) {
  const kind = r.dispositionKind_ButtonGroup;

  if (!kind || kind === 'other') {
    const otherKind = r.dispositionKind_Select;

    if (!otherKind) {
      return null;
    }

    return otherKind;
  }

  return kind;
}

/**
 * Sync the dispositions for a nested target.
 *
 * This is similar to `usePrefetchRecipientDispositions` but instead of fetching
 * the dispositions for each recipient, we use the current form state
 * for the non-root target.
 */
export function useSyncNonRootTargetDispositions({
  survivingSpouseRecipientId,
}: {
  survivingSpouseRecipientId: string | null;
}) {
  const { control } = useFormContext<DispositiveProvisionsFormV2Shape>();

  const { setRecipientDispositions } = useDispositiveDiagramSyncContext();

  const {
    id: targetId,
    isEditingRootTarget,
    householdId,
    isClientProfile,
    primaryClients,
  } = useDispositiveProvisionsContext();

  // Watch both death sections
  const firstDeathRecipients = useWatch({
    control,
    name: 'dispositiveProvisionsV2.firstDeath.recipients' as const satisfies Path<DispositiveProvisionsFormV2Shape>,
  });

  const secondDeathRecipients = useWatch({
    control,
    name: 'dispositiveProvisionsV2.secondDeath.recipients' as const satisfies Path<DispositiveProvisionsFormV2Shape>,
  });

  const recipientIdToMetadata = useRecipientIdMetadataMap();

  /**
   * Convert a form recipient to a second death disposition fragment.
   */
  const secondDeathFragmentFromFormRecipient = useCallback(
    (
      r: DispositiveProvisionsFormRecipient
    ): DispositionsDiagram_DispositionProvisionTerminalFragment | null => {
      if (!r.recipientId) return null;

      // Map SURVIVING_SPOUSE_SENTINEL to actual client profile ID
      const lookupId =
        r.recipientId === SURVIVING_SPOUSE_SENTINEL
          ? survivingSpouseRecipientId
          : r.recipientId;

      if (!lookupId) return null;

      const meta = recipientIdToMetadata[r.recipientId]; // Use original recipientId for metadata lookup
      const recipientKind = (() => {
        if (!meta) return null;

        switch (meta.kind) {
          case undefined:
            return null;
          case 'entity':
            return DispositiveProvisionRecipientKind.Entity;
          case 'testamentaryEntity':
            return DispositiveProvisionRecipientKind.TestamentaryEntity;
          case 'organization':
            return DispositiveProvisionRecipientKind.Organization;
          case 'clientProfile':
            return r.recipientId === SURVIVING_SPOUSE_SENTINEL
              ? DispositiveProvisionRecipientKind.SurvivingSpouse
              : DispositiveProvisionRecipientKind.Individual;
          default:
            throw new UnreachableError({
              case: meta?.kind,
              message: 'Invalid recipient kind',
            });
        }
      })();

      if (!recipientKind) return null;

      const pseudoFragment: DispositionsDiagram_DispositionProvisionTerminalFragment =
        {
          id: lookupId, // Use the actual ID for the fragment
          dispositionKind: getDispositionKindForFormRecipient(r),
          dispositionAmount: r.dispositionAmount ?? null,
          dispositionPercentage: r.dispositionPercentage ?? null,
          associatedHypotheticalWaterfall: null,
          entity:
            recipientKind === DispositiveProvisionRecipientKind.Entity
              ? {
                  household: {
                    id: householdId,
                  },
                  id: lookupId,
                  entityKind: meta?.entityKind,
                  extendedDisplayKind: '',
                  subtype: {
                    id: lookupId,
                    displayName: meta?.displayName ?? '',
                    inEstateStatus: meta?.inEstateStatus,
                  },
                }
              : null,
          individual:
            recipientKind === DispositiveProvisionRecipientKind.Individual ||
            recipientKind === DispositiveProvisionRecipientKind.SurvivingSpouse
              ? {
                  id: lookupId,
                  displayName: meta?.displayName ?? '',
                }
              : null,
          organization:
            recipientKind === DispositiveProvisionRecipientKind.Organization
              ? {
                  id: lookupId,
                  name: meta?.displayName ?? '',
                  clientOrganizationKind: meta?.isCharitableOrganization
                    ? ClientOrganizationKind.CharitableOrganization
                    : ClientOrganizationKind.CorporateTrustee,
                }
              : null,
          testamentaryEntity:
            recipientKind ===
            DispositiveProvisionRecipientKind.TestamentaryEntity
              ? {
                  id: lookupId,
                  displayName: meta?.displayName ?? '',
                  testamentaryEntityKind: meta?.testamentaryEntityKind,
                }
              : null,
          transferTaxKind: r.transferTaxKind,
          recipientKind,
        };

      return pseudoFragment;
    },
    [householdId, recipientIdToMetadata, survivingSpouseRecipientId]
  );

  useEffect(() => {
    // Early exit if we are editing the root target
    if (isEditingRootTarget) return;

    const firstDeathDispositions: DispositionsDiagram_DispositionProvisionTerminalFragment[] =
      (firstDeathRecipients ?? [])
        .map(secondDeathFragmentFromFormRecipient)
        .filter(
          (r): r is DispositionsDiagram_DispositionProvisionTerminalFragment =>
            r !== null
        );

    const secondDeathDispositions: DispositionsDiagram_DispositionProvisionTerminalFragment[] =
      (secondDeathRecipients ?? [])
        .map(secondDeathFragmentFromFormRecipient)
        .filter(
          (r): r is DispositionsDiagram_DispositionProvisionTerminalFragment =>
            r !== null
        );

    setRecipientDispositions(targetId, {
      firstDeath: firstDeathDispositions,
      secondDeath: secondDeathDispositions,
    });
  }, [
    firstDeathRecipients,
    secondDeathRecipients,
    isEditingRootTarget,
    setRecipientDispositions,
    targetId,
    secondDeathFragmentFromFormRecipient,
    isClientProfile,
    primaryClients,
  ]);
}
